import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit } from 'lucide-react';
import { BCS_LEVEL, TAGLEVELS, NOT_SET_FILTER, itemsPerPageOptions, sortBcsTerms, SYLLABUS_STATUS_OPTIONS, SYLLABUS_STATUS_LABELS, SYLLABUS_STATUS_COLORS, BOARD_VALUES, DIVISION_VALUES, UNIT_VALUES, OPTION_KEYS, isBoardLevel, isDivisionLevel, isUnitLevel } from '../utils/Constants';
import QuestionEditForm from './QuestionEditForm';
import QuestionAddForm from './QuestionAddForm';
import MathText from './ui/MathText';
import YearPicker from './ui/YearPicker';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import BulkEditModal from './ingestion/BulkEditModal';
import './components.css';
import { useApiCaller } from '../utils/ApiUtilities';
import {
    getAdminQuestionDeleteUrl,
    getAdminQuestionUrl,
    getBulkSyllabusStatusUrl,
    getQuestionBulkUpdateUrl,
    getTagUrl,
} from '../utils/UrlUtilities';
import { constructBulkSyllabusStatusPayload, constructQuestionBulkUpdatePayload } from '../utils/PayloadUtils';
import { isEmpty } from '../utils/BasicUtilities';
import { formatBd, formatPst, formatBdPst } from '../utils/DateUtilities';

// Render a grid cell's content: its text, plus/or an "<image>" marker when the field
// carries an image (so image-only fields don't show up blank). Long text is truncated to the
// first 100 characters (+ "…") to keep the grid rows compact — the full text is on Edit/Preview.
const CELL_TEXT_LIMIT = 100;
const truncateCell = (text) => {
    const s = text || '';
    return s.length > CELL_TEXT_LIMIT ? `${s.slice(0, CELL_TEXT_LIMIT)}...` : s;
};
const cellWithImage = (text, imageUrl) => {
    const marker = <span className="badge bg-primary-subtle text-primary">&lt;image&gt;</span>;
    const short = truncateCell(text);
    if (text && imageUrl) return <><MathText text={short} /> {marker}</>;
    if (text) return <MathText text={short} />;
    if (imageUrl) return marker;
    return '';
};

const Input = ({ type, placeholder, value, onChange, className, id, min, max, required, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, disabled }) => (
    <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`form-control ${className || ''}`}
        id={id}
        min={min}
        max={max}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
    />
);

// Small marker for the grid's question cell: shows the question type / option count so admins
// see at a glance that an item has extra options (5–8) or is a GRE Text-Completion / Sentence-
// Equivalence item, without expanding it. The grid still shows only the first 4 option columns.
const questionShapeBadge = (question) => {
    const type = question.question_type || 'MCQ';
    if (type === 'TEXT_COMPLETION') {
        const n = (question.metadata && question.metadata.blanks && question.metadata.blanks.length) || 0;
        return <span className="badge bg-info-subtle text-info border border-info-subtle me-1 mb-1" title="GRE Text Completion">TC · {n} blanks</span>;
    }
    if (type === 'SENTENCE_EQUIVALENCE') {
        return <span className="badge bg-info-subtle text-info border border-info-subtle me-1 mb-1" title="GRE Sentence Equivalence (pick 2)">SE · 6 opts</span>;
    }
    if (type === 'TEXT') {
        return <span className="badge bg-secondary-subtle text-secondary me-1 mb-1" title="Written answer">TEXT</span>;
    }
    const count = OPTION_KEYS.filter(k => (question[k] || '').trim()).length;
    if (count > 4) {
        return <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle me-1 mb-1" title={`${count} options`}>{count} opts</span>;
    }
    return null;
};

const Checkbox = ({ onCheckedChange, checked }) => (
    <input
        type="checkbox"
        className="form-check-input"
        onChange={e => onCheckedChange(e)}
        checked={checked}
    />
);

const QUESTIONDIFFICULTY = ["All Difficulties", "EASY", "MEDIUM", "HARD"];


const defaultPageSize = 50;

const QuestionFilterBar = ({ onApplyFilter, onClearFilter }) => {
    // Pending states for filter inputs
    const [pendingLevel, setPendingLevel] = useState('');
    // Multi-level filter: match questions in ANY of these levels. `pendingLevel` mirrors the
    // first entry and drives the dependent (division/board/subject) filters.
    const [pendingLevels, setPendingLevels] = useState([]);
    const [pendingDifficulty, setPendingDifficulty] = useState('');
    const [pendingSearchText, setPendingSearchText] = useState('');
    const [pendingRandom, setPendingRandom] = useState('');
    const [pendingTopic, setPendingTopic] = useState('');
    const [pendingSubject, setPendingSubject] = useState('');
    const [pendingExam, setPendingExam] = useState('');
    const [pendingYear, setPendingYear] = useState('');
    const [pendingExplanation, setPendingExplanation] = useState('');
    const [pendingBcsTerm, setPendingBcsTerm] = useState('');
    const [pendingBoard, setPendingBoard] = useState('');
    const [pendingDivision, setPendingDivision] = useState('');
    const [pendingUnit, setPendingUnit] = useState('');
    const [pendingSyllabusStatus, setPendingSyllabusStatus] = useState('');
    const [pendingDateFrom, setPendingDateFrom] = useState('');
    const [pendingDateTo, setPendingDateTo] = useState('');
    const [pendingQuizTitle, setPendingQuizTitle] = useState('');

    const [tags, setTags] = useState([]);
    const CallApi = useApiCaller();

    const fetchTags = useCallback(async () => {
        try {
            const tagUrl = getTagUrl();
            const tagGetResponse = await CallApi({
                url: tagUrl,
                method: 'GET',
                token: true,
            });
            if (tagGetResponse.success) {
                setTags(tagGetResponse.data.tags);
            } else {
                setTags([]);
            }
        } catch (error) {
            setTags([]);
        }
    }, []); // Added CallApi to dependencies

    useEffect(() => {
        fetchTags();
    }, []); // Fetch tags only once on mount, and re-fetch if CallApi changes

    const handleApply = () => {
        const filters = {
        };
        if (!isEmpty(pendingLevel) || pendingLevels.length) {
            filters["level"] = (pendingLevels.length ? pendingLevels : [pendingLevel]).filter(Boolean).join(',');
        }
        if (!isEmpty(pendingDifficulty)) {
            filters["difficulty"] = pendingDifficulty;
        }
        if (!isEmpty(pendingSearchText)) {
            filters["search"] = pendingSearchText;
        }
        if (!isEmpty(pendingTopic)) {
            filters["topic"] = pendingTopic;
        }
        if (!isEmpty(pendingSubject)) {
            filters["subject"] = pendingSubject;
        }
        if (!isEmpty(pendingExam)) {
            filters["exam"] = pendingExam;
        }
        if (!isEmpty(pendingYear)) {
            filters["year"] = pendingYear;
        }
        if (!isEmpty(pendingBcsTerm)) {
            filters["bcs_term"] = pendingBcsTerm;
        }
        if (!isEmpty(pendingBoard)) {
            filters["board"] = pendingBoard;
        }
        if (!isEmpty(pendingDivision)) {
            filters["division"] = pendingDivision;
        }
        if (!isEmpty(pendingUnit)) {
            filters["unit"] = pendingUnit;
        }
        if (!isEmpty(pendingSyllabusStatus)) {
            filters["syllabus_status"] = pendingSyllabusStatus;
        }
        if (!isEmpty(pendingDateFrom)) {
            filters["created_from"] = pendingDateFrom;
        }
        if (!isEmpty(pendingDateTo)) {
            filters["created_to"] = pendingDateTo;
        }
        if (!isEmpty(pendingQuizTitle)) {
            filters["quiz_title"] = pendingQuizTitle;
        }
        if (!isEmpty(pendingExplanation)) {
            filters["explanation"] = pendingExplanation;
        }
        const randomFilter = pendingRandom === "YES" ? true : (pendingRandom === "NO" ? false : undefined);
        if (!isEmpty(randomFilter)) {
            filters["random"] = randomFilter;
        }
        onApplyFilter(filters);
    };

    const handleClear = () => {
        setPendingLevel('');
        setPendingLevels([]);
        setPendingDifficulty('');
        setPendingSearchText('');
        setPendingRandom('');
        setPendingTopic('');
        setPendingSubject('');
        setPendingExam('');
        setPendingYear('');
        setPendingExplanation('');
        setPendingBcsTerm('');
        setPendingBoard('');
        setPendingDivision('');
        setPendingUnit('');
        setPendingSyllabusStatus('');
        setPendingDateFrom('');
        setPendingDateTo('');
        setPendingQuizTitle('');
        onClearFilter();
    };

    const getTagsForType = (type, currentLevel) => {
        const filtered = currentLevel
            ? tags.filter(tag => tag.level === currentLevel && tag.tag_type === type)
            : tags.filter(tag => tag.tag_type === type);
        // De-dup by name across levels so the dropdown isn't repetitive when no level is set.
        const seen = new Set();
        return filtered.filter(t => {
            if (seen.has(t.name)) return false;
            seen.add(t.name);
            return true;
        });
    };

    return (
        <div className="card shadow-sm mb-4 kp-qfilter">
            <div className="card-body">
                <h5 className="card-title text-dark mb-3">Question Filters</h5>
                <div className="row g-3 align-items-end">
                    {/* Random Filter */}
                    <div className="col-6 col-md-3">
                        <label htmlFor="filterRandom" className="form-label text-gray-700">Random</label>
                        <select
                            id="filterRandom"
                            className="form-select"
                            value={pendingRandom}
                            onChange={(e) => setPendingRandom(e.target.value)}
                        >
                            <option value="">SELECT</option>
                            <option value="YES">YES</option>
                            <option value="NO">NO</option>
                        </select>
                    </div>

                    {/* Level Filter (multi-select: matches any selected level) */}
                    <div className="col-6 col-md-3">
                        <label htmlFor="filterLevel" className="form-label text-gray-700">Level(s)</label>
                        <div className="d-flex flex-wrap gap-1 mb-1">
                            {pendingLevels.map((lv) => (
                                <span key={lv} className="badge bg-primary-subtle text-primary d-flex align-items-center rounded-pill px-2 py-1">
                                    {lv}
                                    <button
                                        type="button"
                                        className="btn-close ms-1 text-danger"
                                        aria-label="Remove"
                                        onClick={() => {
                                            const next = pendingLevels.filter((x) => x !== lv);
                                            setPendingLevels(next);
                                            setPendingLevel(next[0] || '');
                                        }}
                                    ></button>
                                </span>
                            ))}
                        </div>
                        <select
                            id="filterLevel"
                            className="form-select"
                            value=""
                            onChange={(e) => {
                                const v = e.target.value;
                                if (!v || pendingLevels.includes(v)) return;
                                const next = [...pendingLevels, v];
                                setPendingLevels(next);
                                setPendingLevel(next[0]); // primary drives dependent filters
                                // Clear dependent filters on level change (matches prior behavior).
                                setPendingSubject('');
                                setPendingTopic('');
                                setPendingExam('');
                                if (next[0] !== BCS_LEVEL) setPendingBcsTerm('');
                                setPendingBoard('');
                                setPendingDivision('');
                                setPendingUnit('');
                            }}
                        >
                            <option value={""}>
                                {pendingLevels.length ? "Add level…" : "All Levels"}
                            </option>
                            {TAGLEVELS.map((item) => (
                                item.submenu ? (
                                    <optgroup key={item.value} label={item.label}>
                                        {item.submenu.map((sub) => (
                                            <option key={sub.value} value={sub.value}>
                                                {sub.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ) : (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                )
                            ))}
                        </select>
                    </div>

                    {/* Division / Unit / Board — right beside Level, only when applicable. */}
                    {isDivisionLevel(pendingLevel) && (
                        <div className="col-6 col-md-3">
                            <label className="form-label text-gray-700">Division</label>
                            <select className="form-select" value={pendingDivision} onChange={(e) => setPendingDivision(e.target.value)}>
                                <option value="">All Divisions</option>
                                {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                    )}
                    {isUnitLevel(pendingLevel) && (
                        <div className="col-6 col-md-3">
                            <label className="form-label text-gray-700">Unit</label>
                            <select className="form-select" value={pendingUnit} onChange={(e) => setPendingUnit(e.target.value)}>
                                <option value="">All Units</option>
                                {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Difficulty Filter */}
                    <div className="col-6 col-md-3">
                        <label htmlFor="filterDifficulty" className="form-label text-gray-700">Difficulty</label>
                        <select
                            id="filterDifficulty"
                            className="form-select"
                            value={pendingDifficulty}
                            onChange={(e) => setPendingDifficulty(e.target.value)}
                        >
                            {QUESTIONDIFFICULTY.map((diff) => (
                                <option key={diff} value={diff}>
                                    {diff}
                                </option>
                            ))}
                        </select>
                    </div>

                     {/* Search Text Filter */}
                    <div className="col-6 col-md-3">
                        <label htmlFor="filterSearch" className="form-label text-gray-700">Search By Text</label>
                        <Input
                            type="text"
                            id="filterSearch"
                            placeholder="Search in questions..."
                            value={pendingSearchText}
                            onChange={(e) => setPendingSearchText(e.target.value)}
                            className="w-100"
                        />
                    </div>
                </div>

                {/* Tag-driven filters — always visible. When a level is set, dropdown options
                    scope to that level; otherwise they list all distinct tag names. */}
                <div className="row g-3 mt-3 align-items-end">
                    {/* Subject Filter */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterSubject" className="form-label text-gray-700">Subject</label>
                        <select
                            id="filterSubject"
                            className="form-select"
                            value={pendingSubject}
                            onChange={(e) => setPendingSubject(e.target.value)}
                        >
                            <option value="">SELECT</option>
                            <option value={NOT_SET_FILTER}>Not Set</option>
                            {getTagsForType("SUBJECT", pendingLevel).map((tag) => (
                                <option key={tag.tag_id} value={tag.name}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Topic Filter */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterTopic" className="form-label text-gray-700">Topic</label>
                        <select
                            id="filterTopic"
                            className="form-select"
                            value={pendingTopic}
                            onChange={(e) => setPendingTopic(e.target.value)}
                        >
                            <option value="">SELECT</option>
                            <option value={NOT_SET_FILTER}>Not Set</option>
                            {getTagsForType("TOPIC", pendingLevel).map((tag) => (
                                <option key={tag.tag_id} value={tag.name}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Exam Filter */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterExam" className="form-label text-gray-700">Exam</label>
                        <select
                            id="filterExam"
                            className="form-select"
                            value={pendingExam}
                            onChange={(e) => { setPendingExam(e.target.value); if (!e.target.value) setPendingBoard(''); }}
                        >
                            <option value="">SELECT</option>
                            {getTagsForType("EXAM", pendingLevel).map((tag) => (
                                <option key={tag.tag_id} value={tag.name}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Board Filter — beside Exam; only for SSC/HSC and once an exam is picked. */}
                    {isBoardLevel(pendingLevel) && pendingExam && (
                        <div className="col-6 col-md-4">
                            <label className="form-label text-gray-700">Board</label>
                            <select className="form-select" value={pendingBoard} onChange={(e) => setPendingBoard(e.target.value)}>
                                <option value="">All Boards</option>
                                {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                    )}
                    {/* BCS Term Filter — only when filtering BCS questions */}
                    {pendingLevel === BCS_LEVEL && (() => {
                        const bcsTermNames = sortBcsTerms(
                            getTagsForType("BCS_TERM", BCS_LEVEL).map(t => t.name)
                        );
                        return (
                            <div className="col-6 col-md-4">
                                <label htmlFor="filterBcsTerm" className="form-label text-gray-700">BCS Term</label>
                                <select
                                    id="filterBcsTerm"
                                    className="form-select"
                                    value={pendingBcsTerm}
                                    onChange={(e) => setPendingBcsTerm(e.target.value)}
                                >
                                    <option value="">SELECT</option>
                                    <option value={NOT_SET_FILTER}>(unknown)</option>
                                    {bcsTermNames.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })()}
                    {/* Year Filter */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterYear" className="form-label text-gray-700">Exam Year</label>
                        <YearPicker
                            id="filterYear"
                            value={pendingYear === NOT_SET_FILTER ? '' : pendingYear}
                            onChange={setPendingYear}
                            placeholder="All Exam Years"
                            disabled={pendingYear === NOT_SET_FILTER}
                        />
                        <div className="form-check mt-1">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="filterYearNotSet"
                                checked={pendingYear === NOT_SET_FILTER}
                                onChange={(e) => setPendingYear(e.target.checked ? NOT_SET_FILTER : '')}
                            />
                            <label className="form-check-label small text-muted" htmlFor="filterYearNotSet">Not set</label>
                        </div>
                    </div>
                    {/* Explanation Filter — only "Not Set" is meaningful for free-text. */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterExplanation" className="form-label text-gray-700">Explanation</label>
                        <select
                            id="filterExplanation"
                            className="form-select"
                            value={pendingExplanation}
                            onChange={(e) => setPendingExplanation(e.target.value)}
                        >
                            <option value="">SELECT</option>
                            <option value={NOT_SET_FILTER}>Not Set</option>
                        </select>
                    </div>
                    {/* Syllabus status filter */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterSyllabus" className="form-label text-gray-700">Syllabus Status</label>
                        <select
                            id="filterSyllabus"
                            className="form-select"
                            value={pendingSyllabusStatus}
                            onChange={(e) => setPendingSyllabusStatus(e.target.value)}
                        >
                            <option value="">Current + Outdated (default)</option>
                            {SYLLABUS_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    {/* Created-date range filter (inclusive). */}
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterDateFrom" className="form-label text-gray-700">Added From</label>
                        <input
                            id="filterDateFrom"
                            type="date"
                            className="form-control"
                            value={pendingDateFrom}
                            max={pendingDateTo || undefined}
                            onChange={(e) => setPendingDateFrom(e.target.value)}
                        />
                    </div>
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterDateTo" className="form-label text-gray-700">Added To</label>
                        <input
                            id="filterDateTo"
                            type="date"
                            className="form-control"
                            value={pendingDateTo}
                            min={pendingDateFrom || undefined}
                            onChange={(e) => setPendingDateTo(e.target.value)}
                        />
                    </div>
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterQuizTitle" className="form-label text-gray-700">Presence in Test</label>
                        <input
                            id="filterQuizTitle"
                            type="text"
                            className="form-control"
                            placeholder="Test title contains…"
                            value={pendingQuizTitle}
                            onChange={(e) => setPendingQuizTitle(e.target.value)}
                        />
                    </div>
                </div>


                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button onClick={handleApply} className="btn btn-primary">
                        Apply Filters
                    </button>
                    <button onClick={handleClear} className="btn btn-outline-secondary">
                        Clear Filters
                    </button>
                </div>

                {/* Displaying Current Filters */}
                {(pendingLevel !== '' || pendingDifficulty !== '' || pendingSearchText !== '' || pendingRandom !== '' || pendingSubject !== '' || pendingTopic !== '' || pendingExam !== '') && (
                    <p className="mt-3 text-muted small">
                        Current Filters:
                        {pendingRandom !== '' && `Random: "${pendingRandom}"`}
                        {pendingRandom !== '' && (pendingLevel !== '' || pendingDifficulty !== '' || pendingSearchText !== '' || pendingSubject !== '' || pendingTopic !== '' || pendingExam !== '') && <span className="mx-1 fw-bold">AND</span>}
                        {pendingLevel !== '' && `Level: "${TAGLEVELS.find(l => l.value === pendingLevel)?.label}"`}
                        {pendingLevel !== '' && (pendingDifficulty !== '' || pendingSearchText !== '' || pendingSubject !== '' || pendingTopic !== '' || pendingExam !== '') && <span className="mx-1 fw-bold">AND</span>}
                        {pendingDifficulty !== '' && `Difficulty: "${pendingDifficulty}"`}
                        {pendingDifficulty !== '' && (pendingSearchText !== '' || pendingSubject !== '' || pendingTopic !== '' || pendingExam !== '') && <span className="mx-1 fw-bold">AND</span>}
                        {pendingSearchText !== '' && `Search Text: "${pendingSearchText}"`}
                        {pendingSearchText !== '' && (pendingSubject !== '' || pendingTopic !== '' || pendingExam !== '') && <span className="mx-1 fw-bold">AND</span>}
                        {pendingSubject !== '' && `Subject: "${pendingSubject}"`}
                        {pendingSubject !== '' && (pendingTopic !== '' || pendingExam !== '') && <span className="mx-1 fw-bold">AND</span>}
                        {pendingTopic !== '' && `Topic: "${pendingTopic}"`}
                        {pendingTopic !== '' && pendingExam !== '' && <span className="mx-1 fw-bold">AND</span>}
                        {pendingExam !== '' && `Exam: "${pendingExam}"`}
                    </p>
                )}
            </div>
        </div>
    );
};

export function QuestionGrid({ filters, showAddQuestionForm, setShowAddQuestionForm, showEditQuestionForm, setShowEditQuestionForm, editingQuestion, setEditingQuestion, onQuestionsLoaded, savedQuestion }) {
    const [questions, setQuestions] = useState([]);
    const [inGridSearchTerm, setInGridSearchTerm] = useState(''); // New state for in-grid search
    // Debounced copy of the in-grid search, sent to the server so the search covers the WHOLE
    // question table (not just the current page's loaded rows).
    const [gridSearch, setGridSearch] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkBusy, setBulkBusy] = useState(false);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [filteredTotalQuestionCount, setFilteredTotalQuestionCount] = useState(0); // Renamed for clarity
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const CallApi = useApiCaller();

    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Merge filters from props and pagination/random from component state
            if (!isEmpty(filters)) {
                setCurrentPage(1);
            }
            let currentFilters = {
                ...filters,
                page: currentPage,
                limit: pageSize,
            };

            const extraOptions = { bcsTerm: currentFilters.bcs_term };
            if (!isEmpty(currentFilters.board)) extraOptions.board = currentFilters.board;
            if (!isEmpty(currentFilters.division)) extraOptions.division = currentFilters.division;
            if (!isEmpty(currentFilters.unit)) extraOptions.unit = currentFilters.unit;
            if (!isEmpty(currentFilters.syllabus_status)) {
                extraOptions.syllabusStatus = currentFilters.syllabus_status;
            }
            if (!isEmpty(currentFilters.group_id)) {
                extraOptions.groupId = currentFilters.group_id;
            }
            if (!isEmpty(currentFilters.created_from)) extraOptions.createdFrom = currentFilters.created_from;
            if (!isEmpty(currentFilters.created_to)) extraOptions.createdTo = currentFilters.created_to;
            if (!isEmpty(currentFilters.quiz_title)) extraOptions.quizTitle = currentFilters.quiz_title;

            const searchParam = !isEmpty(gridSearch) ? gridSearch : currentFilters.search;
            const questionUrl = getAdminQuestionUrl(
                currentFilters.page,
                currentFilters.limit,
                currentFilters.random,
                currentFilters.level,
                currentFilters.subject,
                currentFilters.topic,
                currentFilters.exam,
                currentFilters.difficulty,
                searchParam,
                currentFilters.year,
                currentFilters.explanation,
                extraOptions
            );

            const questionGetResponse = await CallApi({
                url: questionUrl,
                method: 'GET',
                token: true,
                payload: null, // Pass all filters as queryParams
            });

            if (questionGetResponse.success) {
                setQuestions(questionGetResponse.data.questions);
                setFilteredTotalQuestionCount(questionGetResponse.data.total);
                // Expose the loaded page (+ paging) so the parent can drive Edit-modal Next/Prev.
                if (onQuestionsLoaded) onQuestionsLoaded(questionGetResponse.data.questions || [], { page: currentPage, pageSize });
            } else {
                setQuestions([]);
                setFilteredTotalQuestionCount(0);
                setError(questionGetResponse.message || "Failed to fetch questions.");
            }
        } catch (err) {
            setQuestions([]);
            setFilteredTotalQuestionCount(0);
            setError("An error occurred while fetching questions.");
            console.error('Fetch Questions Error:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filters, gridSearch]); // Re-fetch when pagination or external filters change

    // Debounce the in-grid search into a server query so it searches the whole table (not just
    // the loaded page). Resets to page 1 whenever the term changes.
    useEffect(() => {
        const t = setTimeout(() => {
            setGridSearch(inGridSearchTerm.trim());
            setCurrentPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [inGridSearchTerm]);

    useEffect(() => {
        fetchQuestions();
        // NOTE: showEditQuestionForm is intentionally NOT a dependency — closing the Edit modal
        // must not reload the grid. Edited rows are patched in place via `savedQuestion` instead.
    }, [currentPage, pageSize, filters, gridSearch, showAddQuestionForm]); // eslint-disable-line react-hooks/exhaustive-deps

    // The server already applied the search (whole table) + all filters, so render the page as-is.
    const displayedQuestions = questions;

    // Patch an edited row in place (no full reload) when the Edit modal reports a save.
    useEffect(() => {
        if (!savedQuestion) return;
        setQuestions(prev => prev.map(q => q.question_id === savedQuestion.question_id ? { ...q, ...savedQuestion } : q));
    }, [savedQuestion]);

    const totalPages = Math.ceil(filteredTotalQuestionCount / pageSize);

    const handleDelete = async () => {
        if (!selectedRows || selectedRows.length === 0) return;

        setDeleting(true);
        setError(null);
        try {
            const adminQuestionDeleteUrl = getAdminQuestionDeleteUrl(selectedRows);
            const deleteQuestionResponse = await CallApi({
                url: adminQuestionDeleteUrl,
                method: 'DELETE',
                token: true,
            });

            if (deleteQuestionResponse.success) {
                await fetchQuestions();
                setSelectedRows([]);
            } else {
                setError(deleteQuestionResponse.message || 'Failed to delete questions.');
            }
        } catch (err) {
            setError('An error occurred while deleting the questions.');
            console.error('Delete Question Error:', err);
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };

    const handleEdit = (question) => {
        setEditingQuestion(question);
        setShowEditQuestionForm(true);
    };

    const handleCheckboxChange = (id, event) => {
        setSelectedRows(prev => {
            if (event.target.checked) {
                return [...prev, id];
            } else {
                return prev.filter(rowId => rowId !== id);
            }
        });
    };

    const handleSelectAllChange = (event) => {
        if (event.target.checked) {
            setSelectedRows(displayedQuestions.map(question => question.question_id));
        } else {
            setSelectedRows([]);
        }
    };

    const handlePageChange = (dir) => {
        setCurrentPage(prev => {
            if (totalPages === 0) return 1; // Handle case with no pages
            if (dir === 'prev') {
                return prev === 1 ? totalPages : prev - 1;
            } else if (dir === 'next') {
                return prev === totalPages ? 1 : prev + 1;
            }
            return prev;
        });
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1); // Reset to first page when page size changes
    };

    const handleAdd = () => {
        setShowAddQuestionForm(true);
    };

    const handleCancelAddQuestion = () => {
        setShowAddQuestionForm(false);
    };

    const handleCancelEditQuestion = () => {
        setShowEditQuestionForm(false);
        setEditingQuestion(null);
    };

    const handleSaveQuestion = () => {
        setShowAddQuestionForm(false);
        setShowEditQuestionForm(false);
        setEditingQuestion(null);
        fetchQuestions(); // Re-fetch questions after add/edit
    };

    const handleBulkSyllabus = async () => {
        if (!bulkStatus || selectedRows.length === 0) return;
        setBulkBusy(true);
        const res = await CallApi({
            url: getBulkSyllabusStatusUrl(),
            method: 'POST',
            payload: constructBulkSyllabusStatusPayload({ status: bulkStatus, ids: selectedRows }),
            token: true,
        });
        setBulkBusy(false);
        if (res.success) {
            setSelectedRows([]);
            setBulkStatus('');
            fetchQuestions();
        }
    };

    // Apply a partial taxonomy field set to all selected questions. Returns true
    // on success so the BulkEditModal can close itself.
    const handleBulkEdit = async (fields) => {
        if (selectedRows.length === 0) return false;
        const res = await CallApi({
            url: getQuestionBulkUpdateUrl(),
            method: 'POST',
            payload: constructQuestionBulkUpdatePayload(selectedRows, fields),
            token: true,
        });
        if (res.success) {
            setSelectedRows([]);
            fetchQuestions();
            return true;
        }
        return false;
    };

    return (
        <div className="container-fluid py-4"> {/* Changed to container-fluid for full width */}
            {/* Search in Grid and Action bar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center flex-grow-1 me-3">
                    <label htmlFor="inGridSearch" className="form-label text-nowrap me-2 mb-0">Search:</label>
                    <Input
                        type="text"
                        id="inGridSearch"
                        placeholder="Search the whole question bank…"
                        value={inGridSearchTerm}
                        onChange={(e) => setInGridSearchTerm(e.target.value)}
                        className="flex-grow-1"
                    />
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <button className="btn btn-success" onClick={handleAdd}>Add Question</button>
                    {/* Bulk syllabus-status action on the selected rows */}
                    <select
                        className="form-select form-select-sm w-auto"
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value)}
                        disabled={selectedRows.length === 0}
                        title="Set syllabus status for selected questions"
                    >
                        <option value="">Set syllabus…</option>
                        {SYLLABUS_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button className="btn btn-outline-primary" onClick={handleBulkSyllabus} disabled={!bulkStatus || selectedRows.length === 0 || bulkBusy}>
                        {bulkBusy ? 'Applying…' : `Apply (${selectedRows.length})`}
                    </button>
                    <button className="btn btn-outline-secondary" onClick={() => setShowBulkEdit(true)} disabled={selectedRows.length === 0} title="Edit taxonomy fields on all selected questions at once">Bulk Edit ({selectedRows.length})</button>
                    <button className="btn btn-danger" onClick={() => setConfirmOpen(true)} disabled={selectedRows.length === 0}>Delete Selected ({selectedRows.length})</button>
                </div>
            </div>

            {/* Loading/Error State */}
            {loading && (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-secondary mt-2">Loading questions...</p>
                </div>
            )}
            {error && (
                <div className="alert alert-danger text-center" role="alert">
                    {error}
                </div>
            )}

            {/* Questions Table */}
            {!loading && !error && (
                <div className="card shadow-sm overflow-hidden">
                    {/* Removed maxHeight and overflowY for vertical scrolling of the entire page */}
                    <div className="table-responsive"> {/* Horizontal scroll remains */}
                        <table className="table table-hover mb-0">
                            <thead className="bg-gray-200 text-dark"> {/* Light grey background */}
                                <tr>
                                    <th scope="col" style={{ width: '4%', minWidth: '40px' }} className="p-2 text-center text-uppercase">
                                        <Checkbox
                                            onCheckedChange={handleSelectAllChange}
                                            checked={selectedRows.length > 0 && displayedQuestions.length > 0 && selectedRows.length === displayedQuestions.length}
                                        />
                                    </th>
                                    <th scope="col" style={{ width: '20%', minWidth: '250px' }} className="p-2 text-start text-uppercase">Question Text</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '90px' }} className="p-2 text-start text-uppercase">Level</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '110px' }} className="p-2 text-start text-uppercase">Div / Unit</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '90px' }} className="p-2 text-start text-uppercase">Board</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Subject</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Topic</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Exam</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '90px' }} className="p-2 text-start text-uppercase">Exam Year</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Syllabus</th>
                                    <th scope="col" style={{ width: '5%', minWidth: '60px' }} className="p-2 text-center text-uppercase">Edit</th>
                                    <th scope="col" style={{ width: '10%', minWidth: '160px' }} className="p-2 text-start text-uppercase">Presence in Test</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '70px' }} className="p-2 text-start text-uppercase">Option A</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '70px' }} className="p-2 text-start text-uppercase">Option B</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '70px' }} className="p-2 text-start text-uppercase">Option C</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '70px' }} className="p-2 text-start text-uppercase">Option D</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '70px' }} className="p-2 text-start text-uppercase">Correct Opt</th>
                                    <th scope="col" style={{ width: '15%', minWidth: '200px' }} className="p-2 text-start text-uppercase">Explanation</th>
                                    <th scope="col" style={{ width: '7%', minWidth: '90px' }} className="p-2 text-start text-uppercase">Difficulty</th>
                                    <th scope="col" style={{ width: '9%', minWidth: '150px' }} className="p-2 text-start text-uppercase">Added</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedQuestions.length === 0 ? (
                                    <tr>
                                        <td colSpan="20" className="text-center text-muted py-3">No questions found matching your criteria.</td>
                                    </tr>
                                ) : (
                                    displayedQuestions.map(question => (
                                        <tr key={question.question_id}>
                                            <td style={{ width: '4%' }} className="p-2 text-center">
                                                <Checkbox
                                                    onCheckedChange={(e) => handleCheckboxChange(question.question_id, e)}
                                                    checked={selectedRows.includes(question.question_id)}
                                                />
                                            </td>
                                            <td style={{ width: '20%' }} className="p-2 text-start">
                                                {question.group_id != null && (
                                                    <span
                                                        className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle me-1 mb-1"
                                                        title={`Passage question — group #${question.group_id}${question.group_order != null ? `, order ${question.group_order}` : ''}`}
                                                    >📘 Passage #{question.group_id}{question.group_order != null ? ` · ${question.group_order}` : ''}</span>
                                                )}
                                                {questionShapeBadge(question)}
                                                {question.metadata?.missing_figure
                                                    && !(question.question_image_url || question.explanation_image_url || OPTION_KEYS.some(k => question[`${k}_image_url`])) && (
                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle me-1 mb-1" title="A figure/plot/graph referenced in the source was not captured during extraction">⚠ Figure missing</span>
                                                )}
                                                {cellWithImage(question.question_text, question.question_image_url)}
                                            </td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">{question.level}</td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">
                                                {question.division
                                                    ? <span className="badge bg-primary-subtle text-primary rounded-pill">{question.division}</span>
                                                    : question.unit
                                                        ? <span className="badge bg-primary-subtle text-primary rounded-pill">Unit {question.unit}</span>
                                                        : <span className="text-muted">—</span>}
                                            </td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">
                                                {question.board
                                                    ? <span className="badge bg-secondary-subtle text-secondary rounded-pill">{question.board}</span>
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">
                                                {question.subject && question.subject.split(',').filter(Boolean).map((sub, idx) => (
                                                    <span key={idx} className="badge bg-info-subtle text-info me-1 mb-1 rounded-pill">{sub}</span>
                                                ))}
                                            </td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">
                                                {question.topic && question.topic.split(',').filter(Boolean).map((top, idx) => (
                                                    <span key={idx} className="badge bg-warning-subtle text-warning me-1 mb-1 rounded-pill">{top}</span>
                                                ))}
                                            </td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">
                                                {question.exam && question.exam.split(',').filter(Boolean).map((ex, idx) => (
                                                    <span key={idx} className="badge bg-danger-subtle text-danger me-1 mb-1 rounded-pill">{ex}</span>
                                                ))}
                                            </td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">
                                                {question.bcs_term && (
                                                    <span className="badge bg-info-subtle text-info me-1 mb-1 rounded-pill" title="BCS term">BCS {question.bcs_term}</span>
                                                )}
                                                {question.year && question.year.split(',').filter(Boolean).map((y, idx) => (
                                                    <span key={idx} className="badge bg-secondary-subtle text-secondary me-1 mb-1 rounded-pill">{y}</span>
                                                ))}
                                            </td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">
                                                <span className={`badge bg-${SYLLABUS_STATUS_COLORS[question.syllabus_status] || 'secondary'}`}>
                                                    {SYLLABUS_STATUS_LABELS[question.syllabus_status] || question.syllabus_status || 'Current'}
                                                </span>
                                            </td>
                                            <td style={{ width: '5%' }} className="p-2 text-center">
                                                <div className="d-flex gap-2 justify-content-center align-items-center">
                                                    <button
                                                        className="btn btn-link p-0 text-primary"
                                                        onClick={() => handleEdit(question)}
                                                        title="Edit Question"
                                                    >
                                                        <Edit size={20} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ width: '10%' }} className="p-2 text-start">
                                                {(question.quiz_titles && question.quiz_titles.length > 0) ? (
                                                    question.quiz_titles.length === 1
                                                        ? <span className="small">{question.quiz_titles[0]}</span>
                                                        : (
                                                            <ul className="mb-0 ps-3 small">
                                                                {question.quiz_titles.map((t, idx) => <li key={idx}>{t}</li>)}
                                                            </ul>
                                                        )
                                                ) : <span className="text-muted small">—</span>}
                                            </td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">{cellWithImage(question.option_a, question.option_a_image_url)}</td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">{cellWithImage(question.option_b, question.option_b_image_url)}</td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">{cellWithImage(question.option_c, question.option_c_image_url)}</td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">{cellWithImage(question.option_d, question.option_d_image_url)}</td>
                                            <td style={{ width: '6%' }} className="p-2 text-start">
                                                {question.question_type === 'TEXT' ? (
                                                    <span className="badge rounded-pill px-2 py-1 bg-dark" title={question.text_answer || ''}>&lt;text ans&gt;</span>
                                                ) : (
                                                    <span
                                                        className={`badge rounded-pill px-2 py-1 me-1 ${
                                                        question.correct_option === 'A' ? 'bg-success-subtle text-success' :
                                                        question.correct_option === 'B' ? 'bg-primary-subtle text-primary' :
                                                        question.correct_option === 'C' ? 'bg-warning-subtle text-warning' :
                                                        question.correct_option === 'D' ? 'bg-danger-subtle text-danger' :
                                                        'bg-light text-dark'
                                                        }`}
                                                    >
                                                        {question.correct_option}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ width: '15%' }} className="p-2 text-start">{cellWithImage(question.answer_explanation, question.explanation_image_url)}</td>
                                            <td style={{ width: '7%' }} className="p-2 text-start">{question.difficulty}</td>
                                            <td style={{ width: '9%' }} className="p-2 text-start" title={formatBdPst(question.created_at)}>
                                                <div className="small">{formatBd(question.created_at, { withTime: true })}</div>
                                                <div className="small text-muted">{formatPst(question.created_at, { withTime: true })}</div>
                                                <div className="small text-muted">by {question.created_by?.name || '—'}</div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination Footer */}
            <div className="d-flex justify-content-between align-items-center mt-4">
                {/* Items per page dropdown */}
                <div className="d-flex align-items-center gap-2">
                    <label htmlFor="pageSizeSelect" className="form-label text-secondary mb-0">Items per page:</label>
                    <select
                        id="pageSizeSelect"
                        className="form-select form-select-sm w-auto"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        {itemsPerPageOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>

                {/* Pagination Controls */}
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-outline-secondary rounded-pill"
                        onClick={() => handlePageChange('prev')}
                    >
                        Previous
                    </button>
                    <span className="text-secondary">Page {currentPage} of {totalPages === 0 ? 1 : totalPages} · Total: {filteredTotalQuestionCount}</span>
                    <button
                        className="btn btn-outline-secondary rounded-pill"
                        onClick={() => handlePageChange('next')}
                    >
                        Next
                    </button>
                </div>

            </div>
            <ConfirmDeleteDialog
                open={confirmOpen}
                title="Delete questions"
                message={`Are you sure you want to delete ${selectedRows.length} question(s)? This cannot be undone.`}
                onConfirm={handleDelete}
                onCancel={() => setConfirmOpen(false)}
                busy={deleting}
            />
            <BulkEditModal
                open={showBulkEdit}
                count={selectedRows.length}
                onClose={() => setShowBulkEdit(false)}
                onApply={handleBulkEdit}
            />
        </div>
    );
}

const QuestionManagement = ({ groupId = null, groupDefaults = null, embedded = false, autoOpenAdd = false }) => {
    // Ref to measure the height of the sticky header
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // Applied filters state (passed to QuestionGrid to trigger API call). When scoped to a
    // passage group, group_id is always kept in the filter set so every fetch stays scoped.
    const [appliedFilters, setAppliedFilters] = useState(groupId ? { group_id: groupId } : {});
    // States for showing/hiding modal forms
    const [showAddQuestionForm, setShowAddQuestionForm] = useState(false);
    const [showEditQuestionForm, setShowEditQuestionForm] = useState(false);
    // State to hold the question being edited
    const [editingQuestion, setEditingQuestion] = useState(null);
    // Current grid page (+ paging) mirrored up from QuestionGrid so the Edit modal's Next/Prev
    // can walk the loaded page and show a running serial.
    const [pageQuestions, setPageQuestions] = useState([]);
    const [pageInfo, setPageInfo] = useState({ page: 1, pageSize: 50 });
    // Last edited row (patch), propagated to the grid + nav list so edits show without a reload.
    const [savedQuestion, setSavedQuestion] = useState(null);
    const editIndex = editingQuestion ? pageQuestions.findIndex(q => q.question_id === editingQuestion.question_id) : -1;

    // Merge an edited row into the in-memory nav list + grid (no reload). We deliberately do NOT
    // update `editingQuestion` here so the open form isn't re-seeded (which would clear the
    // "saved" message); reopening/navigating reads the patched grid/nav rows.
    const handleQuestionSaved = (patch) => {
        if (!patch) return;
        setSavedQuestion(patch);
        setPageQuestions(prev => prev.map(q => q.question_id === patch.question_id ? { ...q, ...patch } : q));
    };
    // Right-column source-document viewer (scratch PDF/image). Held here so it survives the
    // Add/Edit modal remounting on Back/Next navigation.
    const [sourceDoc, setSourceDoc] = useState(null);

    // When launched straight from "Add Questions to Passage", pop the add-question modal.
    useEffect(() => {
        if (autoOpenAdd) setShowAddQuestionForm(true);
    }, [autoOpenAdd]);

    const handleApplyFilters = (newFilters) => {
        setAppliedFilters(groupId ? { ...newFilters, group_id: groupId } : newFilters);
    };

    const handleClearFilters = () => {
        setAppliedFilters(groupId ? { group_id: groupId } : {});
    };

    // Effect to measure header height on mount and resize
    useEffect(() => {
        if (headerRef.current) {
            const resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    if (entry.target === headerRef.current) {
                        setHeaderHeight(entry.contentRect.height);
                    }
                }
            });
            resizeObserver.observe(headerRef.current);
            // Initial measurement
            setHeaderHeight(headerRef.current.offsetHeight);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);


    return (
        <div className={`container-fluid p-4 ${ (showAddQuestionForm || showEditQuestionForm) ? 'position-relative' : ''}`}>
            {/* Header Container (now relative, not fixed) */}
            <div ref={headerRef} style={{ backgroundColor: 'white', padding: '1rem 1rem 0 1rem', boxShadow: '0 2px 4px rgba(0,0,0,.05)' }}> {/* Removed fixed positioning and z-index */}
                <h1 className="fs-2 fw-bold text-dark mb-4">{embedded ? 'Passage Questions' : 'Question Management'}</h1>
                <QuestionFilterBar
                    onApplyFilter={handleApplyFilters}
                    onClearFilter={handleClearFilters}
                />
            </div>

            {/* Question Grid (background content, potentially opaqued) */}
            {/* Removed padding-top and margin-top as fixed header compensation is no longer needed */}
            <div className={`${(showAddQuestionForm || showEditQuestionForm) ? 'content-overlay' : ''}`}>
                <QuestionGrid
                    filters={appliedFilters} // Pass applied filters to the grid
                    showAddQuestionForm={showAddQuestionForm}
                    setShowAddQuestionForm={setShowAddQuestionForm}
                    showEditQuestionForm={showEditQuestionForm}
                    setShowEditQuestionForm={setShowEditQuestionForm}
                    editingQuestion={editingQuestion}
                    setEditingQuestion={setEditingQuestion}
                    onQuestionsLoaded={(list, info) => { setPageQuestions(list); if (info) setPageInfo(info); }}
                    savedQuestion={savedQuestion}
                />
            </div>

            {/* Modals are rendered here, on top of the main content */}
            {showAddQuestionForm && (
                <QuestionAddForm
                    groupId={groupId}
                    defaultLevel={groupDefaults?.level || ''}
                    defaultDivision={groupDefaults?.division || ''}
                    defaultUnit={groupDefaults?.unit || ''}
                    defaultSubject={groupDefaults?.subject || ''}
                    defaultTopic={groupDefaults?.topic || ''}
                    defaultExam={groupDefaults?.exam || ''}
                    defaultYear={groupDefaults?.year || ''}
                    passageStartCount={groupDefaults?.questionCount || 0}
                    title={groupId ? 'Add Question to Passage' : 'Add New Question'}
                    cancelLabel={groupId ? 'Discard Creating Passage Questions' : 'Cancel'}
                    addAnotherLabel={groupId ? 'Add Another Question to this Passage' : 'Save to Draft and Add Another'}
                    saveLabel={groupId ? 'Finish Creating the Passage Questions' : 'Save to Draft'}
                    onCancel={() => setShowAddQuestionForm(false)}
                    onSave={() => {
                        setShowAddQuestionForm(false);
                    }}
                    sourceDoc={sourceDoc}
                    setSourceDoc={setSourceDoc}
                />
            )}

            {showEditQuestionForm && (
                <QuestionEditForm
                    question={editingQuestion}
                    onCancel={() => {
                        setShowEditQuestionForm(false);
                        setEditingQuestion(null);
                    }}
                    onSave={() => {
                        setShowEditQuestionForm(false);
                        setEditingQuestion(null);
                        // Re-fetch questions in QuestionGrid after save (handled by fetchQuestions in Grid)
                    }}
                    onBack={() => { if (editIndex > 0) setEditingQuestion(pageQuestions[editIndex - 1]); }}
                    onNext={() => { if (editIndex >= 0 && editIndex < pageQuestions.length - 1) setEditingQuestion(pageQuestions[editIndex + 1]); }}
                    hasPrev={editIndex > 0}
                    hasNext={editIndex >= 0 && editIndex < pageQuestions.length - 1}
                    backLabel="◀ Previous"
                    nextLabel="Next ▶"
                    stayOpenOnSave
                    onSaved={handleQuestionSaved}
                    serialLabel={editIndex >= 0 ? `Question ${(pageInfo.page - 1) * pageInfo.pageSize + editIndex + 1}` : ''}
                    sourceDoc={sourceDoc}
                    setSourceDoc={setSourceDoc}
                />
            )}

            {/* Custom CSS for overlay effect */}
            <style>
                {`
                body {
                    padding-top: 0 !important; /* Ensure no default body padding interferes */
                }
                .content-overlay {
                    filter: blur(2px) grayscale(50%);
                    pointer-events: none;
                    opacity: 0.7;
                    transition: filter 0.3s ease, opacity 0.3s ease;
                }
                .modal.d-block {
                    z-index: 1050;
                }
                /* Sticky header for table scroll (inside table-responsive) */
                .table-responsive thead th {
                    position: sticky;
                    top: 0;
                    z-index: 1; /* Ensure header stays above scrolling content */
                    background-color: #e2e8f0; /* bg-gray-200 Light grey */
                    color: #2d3748; /* dark text for contrast */
                }
                /* Remove column borders */
                .table thead th,
                .table tbody tr td {
                    border-right: none !important;
                    padding-left: 0.75rem; /* Add some padding */
                    padding-right: 0.75rem; /* Add some padding */
                }
                /* Add row separator */
                .table tbody tr {
                    border-bottom: 1px solid #dee2e6;
                }
                .table-hover tbody tr:hover {
                    background-color: #f5f5f5; /* Light gray on hover */
                }
                /* Styles for tag badges */
                .badge.bg-info-subtle { background-color: #cfe2ff; color: #084298; } /* Light blue */
                .badge.bg-warning-subtle { background-color: #fff3cd; color: #664d03; } /* Light yellow */
                .badge.bg-danger-subtle { background-color: #f8d7da; color: #842029; } /* Light red */

                /* Red cross button for tag badges */
                .badge .btn-close.text-danger {
                    font-size: 0.6rem; /* Smaller close button */
                    margin-left: 0.25rem;
                    opacity: 1; /* Ensure it's fully visible */
                    vertical-align: middle;
                    filter: none; /* Remove any filter that might affect color */
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23dc3545'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e");
                }
                .badge .btn-close:hover {
                    opacity: 0.8;
                }
                `}
            </style>
        </div>
    );
};

export { QuestionManagement };
export default QuestionManagement;
